import Fuse from 'fuse.js';
import { Item } from '@/types/models/Item';
import { OpenItem } from '@/types/models/OpenItem';
import { StoreCustomer } from '@/types/models/Customer';
import { PublicStoreItemView } from '@/state/slices/buyerSlice';
import { logger } from '@/lib/logger';

export const itemFuseOptions = {
  keys: [
    { name: 'name', weight: 3 },    // Name has highest priority (weight 3)
    { name: 'name_locale', weight: 2.5 },  // Localized name has high priority
    { name: 'description', weight: 1 }, 
    { 
      name: 'keywords',
      weight: 1,
      getFn: (item: Item) => (item.keywords ? item.keywords.split(',').map(k => k.trim()) : [])
    },
    {
      name: 'categories',
      weight: 1,
      getFn: (item: Item) => (item.categories || [])
    }
  ],
  threshold: 0.3,
  includeScore: true,
  ignoreLocation: true,
  useExtendedSearch: true,
  shouldSort: true
};

export const openItemFuseOptions = {
  keys: [
    { name: 'name', weight: 2 },
    { name: 'keywords', weight: 1 },
  ],
  threshold: 0.2,
  includeScore: true,
  ignoreLocation: true,
  useExtendedSearch: false,
  shouldSort: true,
  minMatchCharLength: 3
};

// Fuse options for public store items (used in buyer pages)
export const publicStoreItemFuseOptions = {
  keys: [
    { name: 'name', weight: 3 },    // Name has highest priority
    { name: 'name_locale', weight: 2.5 },  // Localized name has high priority
    { name: 'description', weight: 1 },  // Description has normal priority
    { 
      name: 'keywords',
      weight: 1,
      getFn: (item: PublicStoreItemView) => {
        if (!item.keywords) return [];
        if (typeof item.keywords === 'string') {
          return item.keywords.split(',').map(k => k.trim());
        }
        return [];
      }
    },
    {
      name: 'categories',
      weight: 1,
      getFn: (item: PublicStoreItemView) => (item.categories || [])
    }
  ],
  threshold: 0.4,  // Slightly higher threshold to allow more fuzzy matches
  includeScore: true,
  ignoreLocation: true,
  useExtendedSearch: true,
  shouldSort: true,
  findAllMatches: false,  // Only return best matches
  minMatchCharLength: 2  // Minimum characters to match
};

export function fuzzySearchItems(
  items: Item[],
  query: string,
  options?: any
): Item[] {
  if (!query) return items;
  const fuse = new Fuse(items, options || itemFuseOptions);
  const results = fuse.search(query);
  results.sort((a, b) => (a.score || 0) - (b.score || 0));
  return results.map(result => result.item);
}

export function fuzzySearchAllItems(
  storeItems: Item[],
  openItems: OpenItem[],
  query: string
): { storeItems: Item[]; openItems: OpenItem[] } {
  if (!query) return { storeItems: [], openItems: [] };
  const itemFuse = new Fuse(storeItems, itemFuseOptions);
  const openItemFuse = new Fuse(openItems, openItemFuseOptions);
  
  const itemResults = itemFuse.search(query);
  const openItemResults = openItemFuse.search(query, { limit: 10 });
  
  const result = {
    storeItems: itemResults.map((result) => result.item),
    openItems: openItemResults.map((result) => result.item),
  };
  logger.debug('fuzzySearchAllItems results', { data: { query, storeItemCount: result.storeItems.length, openItemCount: result.openItems.length }, component: 'fuzzySearch' });
  return result;
}

export function fuzzySearchAllItemsWithFuse(
  itemFuse: Fuse<Item>,
  openItemFuse: Fuse<OpenItem>,
  query: string
): { storeItems: Item[]; openItems: OpenItem[] } {
  if (!query) return { storeItems: [], openItems: [] };
  const itemResults = itemFuse.search(query, { limit: 20 });
  const openItemResults = openItemFuse.search(query, { limit: 10 });
 
  const result = {
    storeItems: itemResults.map((result) => result.item),
    openItems: openItemResults.map((result) => result.item),
  };
  return result;
}

// Preprocess customer_name into name_tokens for word-level prefix search
function preprocessCustomers(customers: StoreCustomer[]): StoreCustomer[] {
  return customers.map(customer => ({
    ...customer,
    name_tokens: customer.customer_name
      ? customer.customer_name.toLowerCase().split(/\s+/)
      : [],
  }));
}

const customerFuseOptions = {
  keys: [
    { name: 'name_tokens', weight: 2 }, // word tokens for prefix search
    { name: 'customer_name', weight: 1 },
    { name: 'customer_phone', weight: 1 },
  ],
  threshold: 0.2, 
  shouldSort: true,
  includeScore: true,
  ignoreLocation: true,
  minMatchCharLength: 2,
  useExtendedSearch: true
};

export function fuzzySearchCustomers(
  customers: StoreCustomer[],
  query: string,
  options?: any
): StoreCustomer[] {
  if (!query) return customers;
  // Preprocess customers to add name_tokens
  const processed = preprocessCustomers(customers);
  const fuse = new Fuse(processed, options || customerFuseOptions);
  const results = fuse.search(query);
  // Print scores rounded to 3 decimal points
  logger.debug('Fuzzy search customers', { data: { query, resultCount: results.length }, component: 'fuzzySearch' });
  return results.map(result => result.item);
}

// Fuzzy search for ledger balances (by customer_name and customer_phone)
const ledgerBalanceFuseOptions = {
  keys: [
    { name: 'customer_name', weight: 2 },
    { name: 'customer_phone', weight: 1 },
  ],
  threshold: 0.3,
  distance: 100,
  includeScore: true,
  useExtendedSearch: true,
};

export function fuzzySearchLedgerBalances(
  balances: { customer_name: string; customer_phone: string }[],
  query: string,
  options?: any
) {
  if (!query) return balances;
  const fuse = new Fuse(balances, options || ledgerBalanceFuseOptions);
  return fuse.search(query).map((result: any) => result.item);
}

// Fuzzy search for public store items (used in buyer pages)
export function fuzzySearchPublicStoreItems(
  items: PublicStoreItemView[],
  query: string,
  options?: any
): PublicStoreItemView[] {
  if (!query) return items;
  const fuse = new Fuse(items, options || publicStoreItemFuseOptions);
  const results = fuse.search(query);
  results.sort((a, b) => (a.score || 0) - (b.score || 0));
  return results.map(result => result.item);
} 